import type { StaticLandingPage } from '../../../content';
import heroImage from './assets/hero-corporate-training.jpg';

export const academyUsPage = {
  slug: 'aca-us-fusa-machinery',
  archetype: 'corporate-b2b',
  body: [
    {
      _uid: 'academy-us-fusa-nav',
      component: 'corporate_sticky_nav_section',
      label: 'TÜV SÜD Academy USA',
      cta_label: 'Request information',
      links: [
        { href: '#why', label: 'Why it matters' },
        { href: '#benefits', label: 'Benefits' },
        { href: '#areas', label: 'Training areas' },
        { href: '#delivery', label: 'Delivery' },
        { href: '#faq', label: 'FAQ' },
        { href: '#contact', label: 'Contact' },
      ],
    },
    {
      _uid: 'academy-us-fusa-hero',
      component: 'corporate_hero_section',
      anchor_id: 'hero',
      title: 'Functional Safety Training for Machinery',
      description_lines: [
        'ISO 13849 • IEC 62061 • IEC 61508 • IEC 61511',
        'Build in-house expertise in functional safety for machinery and industrial systems. TÜV SÜD Academy delivers practical, industry-driven training designed to help your teams design, assess, and maintain safe systems in compliance with international standards.',
      ],
      cta_label: 'Request information',
      form_title: 'Request information',
      form_cta_label: 'Submit request',
      form_labels: {
        name_label: 'Name',
        name_placeholder: 'Your name',
        company_label: 'Company',
        company_placeholder: 'Company name',
        email_label: 'Email',
        email_placeholder: 'email@company.com',
        phone_label: 'Phone',
        phone_placeholder: '+1 000 000 0000',
      },
      hero_image: {
        filename: heroImage,
        alt: 'Functional safety training session',
      },
    },
    {
      _uid: 'academy-us-fusa-trust',
      component: 'certifications_strip_section',
      items: [
        { icon: 'verified', label: 'Over 150 years of TÜV SÜD expertise' },
        { icon: 'workspace_premium', label: 'Internationally recognized certifications' },
        { icon: 'engineering', label: 'Industry-experienced instructors' },
        { icon: 'shield', label: 'Training aligned to global safety standards' },
      ],
    },
    {
      _uid: 'academy-us-fusa-why',
      component: 'corporate_conversion_cta_section',
      anchor_id: 'why',
      headline: 'Why functional safety training matters',
      description_lines: [
        'Functional safety is critical to reducing risk, ensuring regulatory compliance, and protecting people, equipment, and operations. As machinery and automation systems become more complex, organizations need structured training to correctly apply safety standards across the entire lifecycle.',
        'Our functional safety programs help engineering, safety, and automation professionals translate standard requirements into real-world applications.',
      ],
      cta_label: 'Request information',
    },
    {
      _uid: 'academy-us-fusa-benefits',
      component: 'corporate_benefits_section',
      anchor_id: 'benefits',
      headline: 'Benefits of in-house functional safety training',
      benefits: [
        {
          icon: 'tune',
          title: 'Training tailored to your organization',
          description:
            'Programs are designed around your machinery, processes, and industry challenges, ensuring direct relevance to your operations.',
        },
        {
          icon: 'event_available',
          title: 'Flexible delivery options',
          description:
            'Choose the format, schedule, and location that best fit your workforce, including on-site and virtual instructor-led training.',
        },
        {
          icon: 'savings',
          title: 'Efficient use of resources',
          description: 'Train multiple team members at once while reducing travel, downtime, and logistical costs.',
        },
        {
          icon: 'engineering',
          title: 'Industry experts as instructors',
          description:
            'Courses are delivered by professionals with hands-on experience in machinery safety, functional safety lifecycle management, and safety assessments.',
        },
        {
          icon: 'science',
          title: 'Practical, application-focused learning',
          description:
            'Participants work through real examples, calculations, and case studies aligned with industry best practices.',
        },
        {
          icon: 'query_stats',
          title: 'Measurable learning outcomes',
          description:
            'Training effectiveness is evaluated to support continuous improvement and long-term safety performance.',
        },
      ],
    },
    {
      _uid: 'academy-us-fusa-training-areas',
      component: 'training_areas_section',
      anchor_id: 'areas',
      contact_anchor_id: 'contact',
      headline: 'Functional safety training areas',
      description:
        'TÜV SÜD Academy USA offers structured training aligned with key functional safety standards for machinery and industrial applications.',
      areas: [
        {
          icon: 'precision_manufacturing',
          title: 'ISO 13849 & IEC 62061: Functional Safety for the Machinery Sector',
          color: 'var(--ts-semantic-color-background-primary-subtle-default)',
          link_text: 'Learn more',
          link: 'https://www.tuvsud.com/en-us/store/academy-us/transportation-industry/safety/36-40-20-0001',
        },
        {
          icon: 'settings_suggest',
          title: 'IEC 61508: The Basic Functional Safety Standard',
          color: 'var(--ts-semantic-color-background-warning-subtle-default)',
          link_text: 'Learn more',
          link: 'https://www.tuvsud.com/en-us/store/academy-us/transportation-industry/safety/22-05-20-0101',
        },
        {
          icon: 'factory',
          title: 'IEC 61511 - Functional Safety for the Process Industry Certification Program',
          color: 'var(--ts-semantic-color-background-success-subtle-default)',
          link_text: 'Learn more',
          link: 'https://www.tuvsud.com/en-us/store/academy-us/general-industry/safety/36-34-23-0019',
        },
      ],
    },
    {
      _uid: 'academy-us-fusa-delivery',
      component: 'delivery_options_section',
      anchor_id: 'delivery',
      headline: 'Delivery options',
      banner_text: 'We offer training designed to meet your team’s needs:',
      options: [
        {
          number: '1',
          icon: 'cast_for_education',
          title: 'Virtual Classroom',
          description: 'Live, interactive learning from anywhere',
        },
        {
          number: '2',
          icon: 'home_work',
          title: 'In-House Training',
          description: 'Tailored training delivered at your facility',
        },
        {
          number: '3',
          icon: 'groups',
          title: 'Public Courses',
          description: 'Scheduled sessions open to all learners',
        },
        {
          number: '4',
          icon: 'record_voice_over',
          title: 'Private Sessions',
          description: 'Dedicated training for your organization only',
        },
      ],
    },
    {
      _uid: 'academy-us-fusa-faq',
      component: 'corporate_faq_section',
      anchor_id: 'faq',
      headline: 'Frequently asked questions',
      faqs: [
        {
          question: 'What is Functional Safety?',
          answer:
            'Functional safety is, simply put, “protecting a user from technology”. It also protects technology from users. However, more technically the definition of functional safety is: “Systems that lead to the freedom from unacceptable risk of injury or damage to the health of people by the proper implementation of one or more automatic protection functions (often called safety functions). A safety system consists of one or more safety functions.',
        },
        {
          question: 'What is functional safety, and why is it important in machinery systems?',
          answer:
            'Functional safety is a part of the overall safety of a system or equipment, which depends on the correct functioning of safety-related systems. It involves ensuring that machinery behaves as intended in response to inputs, avoiding hazardous events or controlling them effectively when failures or malfunctions occur. In machinery systems, functional safety is crucial because it helps prevent accidents that could result in injury, damage, or fatalities. By implementing safety-related control systems (eg sensors, actuators, and logic controllers), functional safety ensures that risks associated with machinery operation are reduced to acceptable levels. This includes protecting operators, preventing equipment failure, and safeguarding the surrounding environment.',
        },
        {
          question: 'What is the Functional Safety Engineer for ISO 13849 & IEC 62061 Training Course?',
          answer:
            'The Functional Safety Engineer for ISO 13849 & IEC 62061 training course is a 3.5-day program that teaches engineers, technicians, and managers how to design and manage safety-related control systems in machinery. It covers key standards, including ISO 13849 and IEC 62061, with a focus on SIL, performance levels, diagnostics, and compliance. Participants can earn a TÜV SÜD certificate and apply for engineer-level registration in the Functional Safety Certification Program.',
        },
        {
          question: 'What is the significance of IEC 61508 in a Functional Safety Training program?',
          answer:
            'For any kind of industry, functional safety is most fundamental for having a risk-free work environment that also runs efficient work processes at the same time. The IEC 61508 Functional Safety Training program aims at training individuals to have complete know-how with respect to electrical / electronic systems or equipment. This would enable them to have complete understanding and control over the safety control systems. It helps trigger safety systems at the right time to mitigate risks and avoid failures. Such training programs provide companies an opportunity to get their professionals trained as per the standards and ensure a safe work environment free from hazards and risks.',
        },
        {
          question:
            'What is the IEC 61511 Functional Safety for the Process Industry - Safety Instrumented Systems - Certification Program?',
          answer:
            'This certification program is designed as a combined capsule of different modules based on years of practical experience in the field of Functional Safety and participation in relevant standardization committees. The training imparts you required knowledge relating to the existing IEC 61511 standards for safety-relevant electrical/electronic systems.',
        },
      ],
    },
    {
      _uid: 'academy-us-fusa-conversion-cta',
      component: 'corporate_conversion_cta_section',
      headline: 'Build functional safety expertise in-house',
      description:
        'Contact TÜV SÜD Academy USA and receive a training proposal for your machinery and industrial safety teams.',
      cta_label: 'Request information',
    },
    {
      _uid: 'academy-us-fusa-contact',
      component: 'corporate_contact_form_section',
      anchor_id: 'contact',
      headline: 'Request information',
      description:
        'Tell us about your functional safety training needs. Our team will help you identify the most suitable program for your organization.',
      submit_label: 'Submit request',
      form_labels: {
        first_name_label: 'First name',
        first_name_placeholder: 'Your first name',
        last_name_label: 'Last name',
        last_name_placeholder: 'Your last name',
        company_label: 'Company',
        company_placeholder: 'Company name',
        job_title_label: 'Job title',
        job_title_placeholder: 'Your role',
        email_label: 'Business email',
        email_placeholder: 'email@company.com',
        phone_label: 'Phone',
        phone_placeholder: '+1 000 000 0000',
        interest_label: 'Area of interest',
        interest_placeholder: 'Select a training area',
        message_label: 'Message',
        message_placeholder: 'Tell us about your functional safety training needs...',
        privacy_label: 'I accept the privacy policy and the processing of my data',
      },
      success_title: 'Request submitted',
      success_description: 'Our team will contact you within the next 24-48 hours.',
      contact_info_title: 'Contact information',
      reasons_title: 'Why TÜV SÜD Academy?',
      options: [
        { value: 'iso-13849-iec-62061', label: 'ISO 13849 & IEC 62061' },
        { value: 'iec-61508', label: 'IEC 61508' },
        { value: 'iec-61511', label: 'IEC 61511' },
        { value: 'custom', label: 'Customized functional safety training' },
      ],
      contact_items: [
        { icon: 'email', label: 'Email', value: 'academy.us@tuvsud.com' },
        { icon: 'phone', label: 'Phone', value: '+1 978 573 2500' },
        { icon: 'location_on', label: 'Address', value: 'Peabody, Massachusetts, USA' },
      ],
      reasons: [
        'Over 150 years of TÜV SÜD expertise',
        'Internationally recognized certifications',
        'Industry-experienced instructors',
        'Training aligned to global safety standards',
      ],
    },
    {
      _uid: 'academy-us-fusa-footer',
      component: 'corporate_footer_section',
      brand_label: 'TÜV SÜD Academy USA',
      links: [
        { label: 'Terms and Conditions', href: 'https://www.tuvsud.com/en-us/terms-and-conditions' },
        { label: 'Privacy Statement', href: 'https://www.tuvsud.com/en-us/privacy-statement' },
      ],
    },
  ],
} satisfies StaticLandingPage;
